/**
 * Customer-facing reference for a submitted lead.
 *
 * `/api/quotes` returns Odoo's `crm.lead` id on success (`api.quotes.ts`). Shown
 * raw it reads as an internal database id; prefixed it reads as something a
 * customer can quote back to us, which is the whole point of showing it.
 *
 * Deliberately NOT a secret and NOT an authorisation token. The underlying id is
 * sequential, so consecutive submissions reveal roughly how many leads arrive in
 * between. That is acceptable while this string is display-only — nothing accepts
 * it as input. If a status-lookup page is ever built it must authenticate the
 * viewer separately and never treat possession of this reference as proof of
 * anything (VARS-056).
 */
export const QUOTE_REFERENCE_PREFIX = "VQ";

export function formatQuoteReference(leadId: unknown): string | null {
  // A missing id is normal, not exceptional: Odoo can accept the lead and still
  // return no id, and older deployments of `varsco_content_api` did. The caller
  // renders the thank-you without a reference rather than blocking on it or
  // showing "VQ-undefined".
  if (typeof leadId === "number") {
    return Number.isInteger(leadId) && leadId > 0 ? `${QUOTE_REFERENCE_PREFIX}-${leadId}` : null;
  }
  if (typeof leadId === "string") {
    const trimmed = leadId.trim();
    // Digits only. Anything else came from somewhere unexpected and is not
    // echoed back into the page.
    return /^[0-9]+$/.test(trimmed) && Number(trimmed) > 0
      ? `${QUOTE_REFERENCE_PREFIX}-${Number(trimmed)}`
      : null;
  }
  return null;
}

/** Reads `lead_id` out of an `/api/quotes` success body, tolerating any shape. */
export function referenceFromResponse(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  return formatQuoteReference((body as { lead_id?: unknown }).lead_id);
}
